import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Dropdown, Col, Card, Button, Tooltip, Table, Space } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { NumberFormatter } from "../../../../../../../utils/validator";
import { useStateGlobalContext } from "../../../../../../../contexts/GlobalContext";
import { doPatch } from "../../../../../../../API/apis";
import CollectPayment from "./components/CollectPayment";
import ServiceInvoicePad from "./components/ServiceInvoicePad";
import CustomInvoice from "./components/CustomInvoice";
import ViewInvoice from "./components/ViewInvoice";
import showConfirmModal from "../../../../../../../components/ModalConfirm/ModalConfirm";
import Notification from "../../../../../../../components/Notification/Notification";
import FormatDateTime from "../../../../../../../components/FormatDate/FormatDateTime";
import CustomTable from "../../../../../../../components/CustomTable/CustomTable";
import { invoiceActions, invoiceStatus } from "../../../../../../../utils/Data";
import { InvoiceStatus } from "../../../../../../../components/GlobalComponents/StatusLabels";
import SelectedInvoicesPrint from "./components/SelectedInvoicesPrint";
import { generateServiceInvoicePDF } from "./DownloadServiceInvoice";
import { generateMultipleServiceInvoicesPDF } from "./DownloadMultipleInvoices";

function ServiceInvoices() {
  const { id } = useParams();
  const [visible, setVisible] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serviceDrawer, setServiceDrawer] = useState(false);
  const [servicesData, setServicesData] = useState(null);
  const { invoices, getInvoices, profile, patientBasicInfo, getPatientData, officeSetting, getOfficeSettings } = useStateGlobalContext();
  const [showCustomInvoice, setShowCustomInvoice] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(false);
  const [viewinvoiceData, setViewInvoiceData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedInvoiceDrawerOpen, setSelectedInvoicesDrawerOpen] =
    useState(false);

  const role = profile && profile?.user_info[0]?.role;

  const coloumn = [
    {
      key: 2,
      title: "Invoice ID",
      ellipsis: true,
      dataIndex: "invoice_id",
      align: "center",
    },
    {
      key: 3,
      title: "Service Name",
      ellipsis: true,
      align: "center",
      width: "15%",
      dataIndex: "service_name",
      render: (text, record) => (
        <Button type="link" onClick={() => handleInvoiceView(record)}>
          {text}
        </Button>
      ),
    },
    {
      key: 4,
      title: "Reason For Visit",
      dataIndex: "visit_reason",
    },
    {
      key: 5,
      title: "Issue Date",
      ellipsis: true,
      dataIndex: "issue_date",
      align: "center",
      width: "15%",
      render: (text) => (
        <Tooltip title={<FormatDateTime date={text} type="time" />}>
          <span>{text ? <FormatDateTime date={text} type="date" /> : ""}</span>
        </Tooltip>
      ),
    },

    {
      key: 6,
      title: "Sub Total",
      ellipsis: true,
      align: "center",
      width: "15%",
      dataIndex: "sub_total",
      render: (text, record) => (
        <>
          <NumberFormatter value={text === 0 ? record?.price : text} />
        </>
      ),
    },
    {
      key: 7,
      title: "Status",
      dataIndex: "status",
      width: "10%",
      ellipsis: true,
      align: "center",
      filters: invoiceStatus?.map((status) => ({
        text: status.label,
        value: status.label,
      })),
      onFilter: (value, record) => record.status === value,
      defaultFilteredValue: ["Paid", "unpaid"],
      render: (status) => <InvoiceStatus status={status} />,
    },
  ];

  if (role !== "biller") {
    coloumn.push({
      title: "Action",
      key: "action",
      fixed: "right",
      align: "center",
      width: "10%",
      render: (_, record) => {
        const { status } = record;
        const availableItems =
          status === "Paid"
            ? invoiceActions.filter((item) => [1, 3, 4, 5].includes(item.key))
            : status === "unpaid"
            ? invoiceActions.filter((item) => [1, 2, 3, 4, 5].includes(item.key))
            : status === "Cancelled"
            ? invoiceActions.filter((item) => [1, 3].includes(item.key))
            : invoiceActions?.filter((item) => [1, 3, 4].includes(item.key));
        return (
          <Dropdown
            menu={{
              items: availableItems.map((item) => ({
                ...item,
                onClick: () => {
                  if (item.key === 4) {
                    handleCancelInvoice(record);
                  } else if (item.key === 2) {
                    handleView(record);
                  } else if (item.key === 1) {
                    handleInvoiceView(record);
                  } else if (item.key === 3) {
                    handlePrint(record);
                  } else if (item.key === 5) {
                    handleDownloadPDF(record);
                  }
                },
              })),
            }}
            trigger={["click"]}
          >
            <EllipsisOutlined className="cursor-pointer" />
          </Dropdown>
        );
      },
    });
  }

  const handlePrint = (record) => {
    setServiceDrawer(true);
    setServicesData(record);
  };

  const closePrint = () => {
    setServiceDrawer(false);
  };

  const handleView = (record) => {
    setInvoice(record);
    setVisible(true);
  };

  const onClose = () => {
    setTimeout(() => {
      setVisible(false);
    }, 200);
  };

  const handleInvoiceView = (record) => {
    setViewInvoice(true);
    setViewInvoiceData(record);
  };

  const closeInvoiceView = () => {
    setViewInvoice(false);
  };

  const openCustomInvoice = () => {
    setShowCustomInvoice(true);
  };

  const closeCusomInvoice = () => {
    setShowCustomInvoice(false);
  };

  const handleCancelInvoice = (record) => {
    showConfirmModal({
      title: "Confirm Cancel?",
      icon: null,
      content: "Are you sure you want to Cancel this?",
      okText: "Cancel Invoice",
      okType: "danger",
      cancelText: "Cancel",
      className: "custom-modal",
      onOk: async () => {
        try {
          const response = await doPatch(
            `/invoice/invoice-update/${id}/${record?.id}/`,
            { status: "Cancelled" }
          );
          if ((response.status = 200)) {
            Notification.success("Patient Invoice Cancelled Successfully");
            getInvoices(id);
          }
        } catch (error) {
          console.log(error);
        }
      },
    });
  };

  const handlePrintSelected = () => {
    setSelectedInvoicesDrawerOpen(true);
    setServicesData(selectedRowKeys);
  };

  const handleCloseInvoicesDrawer = () => {
    setSelectedInvoicesDrawerOpen(false);
    setServicesData(null);
  };

  const rowSelection = {
    columnWidth: "30px",
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRows);
    },
  };

  const handleDownloadPDF = async (record) => {
    try {
      // Ensure patient & office data is available
      if (!patientBasicInfo) {
        await getPatientData(id);
      }
      if (!officeSetting) {
        await getOfficeSettings();
      }

      await generateServiceInvoicePDF(record, patientBasicInfo, officeSetting);
      Notification.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      Notification.error("Failed to generate PDF");
    }
  };

  // Download a single consolidated PDF for all selected invoices
  const handleDownloadSelectedPDF = async () => {
    try {
      if (selectedRowKeys.length === 0) return;

      // Ensure patient & office data is available
      if (!patientBasicInfo) {
        await getPatientData(id);
      }
      if (!officeSetting) {
        await getOfficeSettings();
      }

      await generateMultipleServiceInvoicesPDF(
        selectedRowKeys,
        patientBasicInfo,
        officeSetting
      );
      Notification.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error generating multiple-invoice PDF:", error);
      Notification.error("Failed to generate PDF");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await getInvoices(id);
      setLoading(false);
    };
    fetchData(id);
  }, [id]);

  return (
    <>
      <Card className="shadow-class"
       title="Services Invoices"
       extra={
        <Space>
          <div className="flex gap-2">
            {selectedRowKeys.length !== 0 && (
              <>
                <Button
                  type="default"
                  onClick={handlePrintSelected}
                  disabled={selectedRowKeys.length === 0}
                >
                  Print Selected
                </Button>
                <Button
                  type="primary"
                  onClick={handleDownloadSelectedPDF}
                  disabled={selectedRowKeys.length === 0}
                >
                  Download PDF
                </Button>
              </>
            )}
            {role !== "biller" && (
              <Button type="primary" onClick={openCustomInvoice}>
                Add Custom Invoice
              </Button>
            )}
          </div>
        </Space>
       }
      >
        <CustomTable
          loading={loading}
          dataSource={invoices}
          columns={coloumn}
          rowSelection={rowSelection}
          rowKey="id"
        />
      </Card>

      {visible && (
        <CollectPayment visible={visible} onClose={onClose} invoice={invoice} />
      )}
      {serviceDrawer && (
        <ServiceInvoicePad
          visible={serviceDrawer}
          onClose={closePrint}
          rxData={servicesData}
        />
      )}
      <CustomInvoice visible={showCustomInvoice} onClose={closeCusomInvoice} />
      {viewInvoice && (
        <ViewInvoice
          visible={viewInvoice}
          onClose={closeInvoiceView}
          rxData={viewinvoiceData}
        />
      )}
      {selectedInvoiceDrawerOpen && (
        <SelectedInvoicesPrint
          visible={selectedInvoiceDrawerOpen}
          onClose={handleCloseInvoicesDrawer}
          data={servicesData}
        />
      )}
    </>
  );
}

export default ServiceInvoices;
